import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, User, FileSignature, FileSpreadsheet, Music, Percent, FolderOpen, Loader2, Mail, Phone, MapPin, CreditCard, IdCard, Cake, Save, FileDown, CheckCircle2, Trash2, Eye, Upload as UploadIcon } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { fetchProvisions, downloadProvision, markProvisionPaid, deleteProvision } from "../lib/provisionEngine";
import { deleteArtist } from "../lib/driveClient";
import { DeleteEntityButton } from "../components/DeleteEntityButton";

const formatEUR = (v) => (Number(v || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const TABS = [
  { id: "overview", label: "Übersicht", icon: User },
  { id: "contracts", label: "Verträge", icon: FileSignature },
  { id: "invoices", label: "Rechnungen", icon: FileSpreadsheet },
  { id: "musichub", label: "MusicHub", icon: Music },
  { id: "provisions", label: "Provisionen", icon: Percent },
  { id: "files", label: "Dateien", icon: FolderOpen },
];

export default function DriveArtistDetail() {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(null);
  const [dat, setDat] = useState({ contracts: [], invoices: [], musichub: [], provisions: [], files: [] });
  const [busy, setBusy] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, c, i, mh, p, f] = await Promise.all([
        supabase.from("drive_artists").select("*").eq("id", id).single(),
        supabase.from("drive_contracts").select("*").eq("artist_id", id).order("created_at", { ascending: false }),
        supabase.from("drive_invoices").select("*").eq("artist_id", id).order("invoice_date", { ascending: false }),
        // MusicHub-Payouts sind global (kein artist_id) — leer belassen
        Promise.resolve({ data: [] }),
        fetchProvisions({ artist_id: id }),
        supabase.from("drive_files").select("*").eq("artist_id", id).order("uploaded_at", { ascending: false }).then(r => r).catch(() => ({ data: [] })),
      ]);
      if (a.error) throw a.error;
      setArtist(a.data);
      setEdit(a.data);
      setDat({ contracts: c.data || [], invoices: i.data || [], musichub: mh.data || [], provisions: p, files: f.data || [] });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const saveArtist = async () => {
    setSaving(true);
    try {
      const patch = {
        first_name: edit.first_name, last_name: edit.last_name, artist_name: edit.artist_name,
        email: edit.email, phone: edit.phone, address: edit.address, zip_city: edit.zip_city,
        birthdate: edit.birthdate || null, tax_id: edit.tax_id, iban: edit.iban, bic: edit.bic,
        bank_name: edit.bank_name, commission_percent: Number(edit.commission_percent) || 40,
        notes: edit.notes,
      };
      const { error } = await supabase.from("drive_artists").update(patch).eq("id", id);
      if (error) throw error;
      toast.success("Gespeichert");
      await loadAll();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading || !artist) {
    return <div className="flex items-center gap-3 text-zinc-400 text-sm py-16"><Loader2 size={16} className="animate-spin" /> Lade Künstler…</div>;
  }

  const displayName = [artist.first_name, artist.last_name].filter(Boolean).join(" ") || artist.artist_name;
  const totalProv = dat.provisions.reduce((s, p) => s + Number(p.artist_amount || 0), 0);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <Link to="/drive/crm" className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white font-bold mb-4">
          <ArrowLeft size={12} /> Zurück zum CRM
        </Link>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-2">Künstler</div>
            <div className="font-display font-black uppercase text-3xl md:text-4xl mb-2">{artist.artist_name || displayName}</div>
            <div className="text-sm text-zinc-400">{displayName}{artist.customer_number ? ` · ${artist.customer_number}` : ""}</div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-right">
            <div><div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Verträge</div><div className="text-xl font-black text-white">{dat.contracts.length}</div></div>
            <div><div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Provisionen</div><div className="text-xl font-black text-white">{dat.provisions.length}</div></div>
            <div><div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Ausgezahlt</div><div className="text-xl font-black text-[#ff0033]">{formatEUR(totalProv)}</div></div>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <DeleteEntityButton
            label="Künstler löschen"
            entityName={artist.artist_name || displayName}
            onConfirm={(withFiles) => deleteArtist(id, { withFiles })}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/[0.06] overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-[10px] tracking-[0.3em] uppercase font-bold border-b-2 transition-colors whitespace-nowrap ${active ? "border-[#ff0033] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field icon={<User size={13} />} label="Künstlername" value={edit.artist_name} onChange={(v) => setEdit({ ...edit, artist_name: v })} />
          <Field icon={<User size={13} />} label="Vorname" value={edit.first_name} onChange={(v) => setEdit({ ...edit, first_name: v })} />
          <Field icon={<User size={13} />} label="Nachname" value={edit.last_name} onChange={(v) => setEdit({ ...edit, last_name: v })} />
          <Field icon={<Cake size={13} />} label="Geburtsdatum" type="date" value={edit.birthdate || ""} onChange={(v) => setEdit({ ...edit, birthdate: v })} />
          <Field icon={<Mail size={13} />} label="E-Mail" value={edit.email || ""} onChange={(v) => setEdit({ ...edit, email: v })} />
          <Field icon={<Phone size={13} />} label="Telefon" value={edit.phone || ""} onChange={(v) => setEdit({ ...edit, phone: v })} />
          <Field icon={<MapPin size={13} />} label="Adresse" value={edit.address || ""} onChange={(v) => setEdit({ ...edit, address: v })} />
          <Field icon={<MapPin size={13} />} label="PLZ / Ort" value={edit.zip_city || ""} onChange={(v) => setEdit({ ...edit, zip_city: v })} />
          <Field icon={<IdCard size={13} />} label="Steuer-ID" value={edit.tax_id || ""} onChange={(v) => setEdit({ ...edit, tax_id: v })} />
          <Field icon={<Percent size={13} />} label="Label-Anteil (%)" type="number" step="0.5" value={edit.commission_percent ?? 40} onChange={(v) => setEdit({ ...edit, commission_percent: v })} />
          <Field icon={<CreditCard size={13} />} label="IBAN" value={edit.iban || ""} onChange={(v) => setEdit({ ...edit, iban: v })} />
          <Field icon={<CreditCard size={13} />} label="BIC" value={edit.bic || ""} onChange={(v) => setEdit({ ...edit, bic: v })} />
          <Field icon={<CreditCard size={13} />} label="Bank" value={edit.bank_name || ""} onChange={(v) => setEdit({ ...edit, bank_name: v })} />
          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Notizen</label>
            <textarea value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} rows={3} className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
          </div>
          <div className="md:col-span-2">
            <button onClick={saveArtist} disabled={saving} data-testid="save-artist" className="inline-flex items-center gap-2 bg-[#ff0033] text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:shadow-[0_0_20px_rgba(255,0,51,0.5)] transition-all disabled:opacity-50">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {saving ? "Speichere…" : "Änderungen speichern"}
            </button>
          </div>
        </div>
      )}

      {tab === "contracts" && (
        <>
          <ContractUploader entityType="artist" entityId={id} onDone={loadAll} />
          <SimpleList
            rows={dat.contracts.map(c => ({ id: c.id, primary: c.title || c.category, secondary: (c.status || "").toUpperCase(), date: c.created_at, path: c.signed_storage_path || c.storage_path }))}
            onDelete={async (r) => { await deleteContractRow(r); toast.success("Gelöscht"); await loadAll(); }}
            empty="Keine Verträge für diesen Künstler."
          />
        </>
      )}

      {tab === "invoices" && (
        <SimpleList
          rows={dat.invoices.map(i => ({ id: i.id, primary: i.invoice_number, secondary: `${(i.kind || "").toUpperCase()} · ${formatEUR(i.total)}`, date: i.invoice_date, path: i.storage_path }))}
          empty="Keine Rechnungen für diesen Künstler."
        />
      )}

      {tab === "musichub" && (
        <div className="bg-[#0A0A0A] border border-white/[0.06] px-6 py-8 text-center text-sm text-zinc-500 italic">
          MusicHub-Payouts sind zentral abgelegt (nicht pro Künstler). Die daraus entstandenen Provisionsabrechnungen findest du im Tab „Provisionen".
        </div>
      )}

      {tab === "provisions" && (
        <div className="space-y-3" data-testid="provisions-tab">
          {dat.provisions.length === 0 ? (
            <div className="bg-[#0A0A0A] border border-white/[0.06] px-6 py-8 text-center text-sm text-zinc-500 italic">Noch keine Provisionsabrechnungen.</div>
          ) : (
            dat.provisions.map((p) => (
              <div key={p.id} className="bg-[#0A0A0A] border border-white/[0.06] px-5 py-3 grid grid-cols-1 md:grid-cols-[140px_1fr_130px_130px_auto] gap-3 items-center">
                <div>
                  <div className="text-sm text-white font-bold">{p.statement_number}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{p.month}</div>
                </div>
                <div className="text-xs text-zinc-500 truncate">{p.source_label || "—"}</div>
                <div className="text-right text-sm text-zinc-300 tabular-nums"><div className="text-[10px] text-zinc-500">Brutto</div>{formatEUR(p.gross_amount)}</div>
                <div className="text-right text-sm text-[#ff0033] font-bold tabular-nums"><div className="text-[10px] text-zinc-500 font-normal">Auszahlung</div>{formatEUR(p.artist_amount)}</div>
                <div className="flex items-center gap-2 justify-end">
                  {p.status !== "paid" && (
                    <button onClick={async () => { setBusy(`paid-${p.id}`); try { await markProvisionPaid(p.id); toast.success("Als bezahlt markiert"); await loadAll(); } catch (e) { toast.error(e.message); } finally { setBusy(null); } }} disabled={busy === `paid-${p.id}`} title="Als bezahlt markieren" className="inline-flex items-center justify-center p-2 border border-white/10 text-zinc-500 hover:border-emerald-500/50 hover:text-emerald-400 transition-colors disabled:opacity-40">
                      {busy === `paid-${p.id}` ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                    </button>
                  )}
                  <button onClick={async () => { if (!p.storage_path) { toast.error("Keine Datei"); return; } setBusy(`pv-${p.id}`); try { const { data, error } = await supabase.storage.from("drive-files").createSignedUrl(p.storage_path, 300); if (error) throw error; window.open(data.signedUrl, "_blank", "noopener,noreferrer"); } catch (e) { toast.error(e.message); } finally { setBusy(null); } }} disabled={busy === `pv-${p.id}`} title="Vorschau" className="inline-flex items-center justify-center p-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white transition-colors disabled:opacity-40">
                    {busy === `pv-${p.id}` ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
                  </button>
                  <button onClick={async () => { setBusy(`dl-${p.id}`); try { await downloadProvision(p); } catch (e) { toast.error(e.message); } finally { setBusy(null); } }} disabled={busy === `dl-${p.id}`} title="PDF" className="inline-flex items-center justify-center p-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white transition-colors disabled:opacity-40">
                    {busy === `dl-${p.id}` ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />}
                  </button>
                  <button onClick={async () => { if (!window.confirm(`Provisionsabrechnung ${p.statement_number} wirklich löschen?`)) return; setBusy(`del-${p.id}`); try { await deleteProvision(p); toast.success("Gelöscht"); await loadAll(); } catch (e) { toast.error(e.message); } finally { setBusy(null); } }} disabled={busy === `del-${p.id}`} title="Löschen" className="inline-flex items-center justify-center p-2 border border-white/10 text-zinc-500 hover:border-[#ff0033]/50 hover:text-[#ff0033] transition-colors disabled:opacity-40">
                    {busy === `del-${p.id}` ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "files" && (
        <>
          <FileUploader entityType="artist" entityId={id} onDone={loadAll} />
          <SimpleList
            rows={dat.files.map(f => ({ id: f.id, primary: f.name, secondary: `${((f.size || 0) / 1024).toFixed(0)} KB`, date: f.uploaded_at, path: f.storage_path }))}
            onDelete={async (r) => { await deleteFileRow(r); toast.success("Gelöscht"); await loadAll(); }}
            empty="Keine Dateien für diesen Künstler."
          />
        </>
      )}
    </div>
  );
}

const Field = ({ icon, label, value, onChange, type = "text", step }) => (
  <div>
    <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2 flex items-center gap-2">{icon} {label}</label>
    <input type={type} step={step} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
  </div>
);

export const SimpleList = ({ rows, empty, onDelete }) => {
  const [busy, setBusy] = useState(null);
  const download = async (path, name) => {
    if (!path) { toast.error("Keine Datei"); return; }
    setBusy(path);
    try {
      const { data: blob, error } = await supabase.storage.from("drive-files").download(path);
      if (error) throw error;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name || "download.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };
  const preview = async (path) => {
    if (!path) { toast.error("Keine Datei"); return; }
    setBusy(`pv-${path}`);
    try {
      const { data, error } = await supabase.storage.from("drive-files").createSignedUrl(path, 300);
      if (error) throw error;
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };
  const del = async (row) => {
    if (!window.confirm(`"${row.primary}" wirklich löschen?`)) return;
    setBusy(`del-${row.id}`);
    try { await onDelete(row); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };
  if (!rows.length) return <div className="bg-[#0A0A0A] border border-white/[0.06] px-6 py-8 text-center text-sm text-zinc-500 italic">{empty}</div>;
  return (
    <div className="bg-[#0A0A0A] border border-white/[0.06] divide-y divide-white/[0.04]">
      {rows.map((r) => (
        <div key={r.id} className="px-5 py-3 grid grid-cols-1 md:grid-cols-[1fr_160px_100px_auto] gap-3 items-center">
          <div className="text-sm text-white font-medium truncate">{r.primary}</div>
          <div className="text-xs text-zinc-400">{r.secondary}</div>
          <div className="text-[10px] text-zinc-500 tracking-wide">{r.date ? new Date(r.date).toLocaleDateString("de-DE") : ""}</div>
          <div className="flex items-center gap-2 justify-end">
            <button onClick={() => preview(r.path)} disabled={busy === `pv-${r.path}`} title="Vorschau" className="inline-flex items-center justify-center p-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white transition-colors disabled:opacity-40">
              {busy === `pv-${r.path}` ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
            </button>
            <button onClick={() => download(r.path, `${r.primary}.pdf`)} disabled={busy === r.path} title="Download" className="inline-flex items-center justify-center p-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white transition-colors disabled:opacity-40">
              {busy === r.path ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />}
            </button>
            {onDelete && (
              <button onClick={() => del(r)} disabled={busy === `del-${r.id}`} title="Löschen" className="inline-flex items-center justify-center p-2 border border-white/10 text-zinc-500 hover:border-[#ff0033]/50 hover:text-[#ff0033] transition-colors disabled:opacity-40">
                {busy === `del-${r.id}` ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Löschhelfer für File/Contract-Zeilen
export async function deleteFileRow(row) {
  if (row.path) await supabase.storage.from("drive-files").remove([row.path]).catch(() => {});
  await supabase.from("drive_files").delete().eq("id", row.id);
}
export async function deleteContractRow(row) {
  if (row.path) await supabase.storage.from("drive-files").remove([row.path]).catch(() => {});
  await supabase.from("drive_contracts").delete().eq("id", row.id);
}

// Direkt-Upload eines beliebigen Files für Künstler/Kunde/Externe (Dateien-Tab)
export const FileUploader = ({ entityType, entityId, onDone }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const safe = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
      const path = `contact-files/${entityType}/${entityId}/${Date.now()}-${safe}`;
      const up = await supabase.storage.from("drive-files").upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
      if (up.error) throw up.error;
      const link = { artist: "artist_id", customer: "customer_id", external: "external_id", feature: "feature_id", creator: "creator_id" }[entityType];
      const insert = { name: file.name, storage_path: path, size: file.size, mime_type: file.type };
      insert[link] = entityId;
      const { error } = await supabase.from("drive_files").insert([insert]);
      if (error) throw error;
      toast.success("Datei hochgeladen");
      onDone?.();
    } catch (e) { toast.error(e.message); }
    finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mb-4 flex items-center justify-end">
      <input ref={inputRef} type="file" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={`upload-file-${entityType}`}
        className="inline-flex items-center gap-3 bg-[#ff0033] text-white px-5 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:shadow-[0_0_20px_rgba(255,0,51,0.5)] transition-all disabled:opacity-50"
      >
        {uploading ? <Loader2 size={13} className="animate-spin" /> : <UploadIcon size={13} />}
        {uploading ? "Lädt hoch…" : "Datei hochladen"}
      </button>
    </div>
  );
};
// entityType: "artist" | "customer" | "external"
export const ContractUploader = ({ entityType, entityId, onDone }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Bitte eine PDF-Datei wählen.");
      return;
    }
    setUploading(true);
    try {
      const safe = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
      const path = `contracts/${entityType}/${entityId}/${Date.now()}-${safe}`;
      const up = await supabase.storage.from("drive-files").upload(path, file, { contentType: "application/pdf", upsert: false });
      if (up.error) throw up.error;
      const title = file.name.replace(/\.pdf$/i, "");
      const link = { artist: "artist_id", customer: "customer_id", external: "external_id", feature: "feature_id", creator: "creator_id" }[entityType];
      const insert = {
        title,
        category: "uploaded",
        status: "signed",
        storage_path: path,
        signed_storage_path: path,
        body_markdown: `# ${title}\n\n_Extern hochgeladenes PDF-Dokument._`,
      };
      insert[link] = entityId;
      const { error } = await supabase.from("drive_contracts").insert([insert]);
      if (error) throw error;
      toast.success("Vertrag hochgeladen");
      onDone?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mb-4 flex items-center justify-end">
      <input ref={inputRef} type="file" accept="application/pdf" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={`upload-contract-${entityType}`}
        className="inline-flex items-center gap-3 bg-[#ff0033] text-white px-5 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:shadow-[0_0_20px_rgba(255,0,51,0.5)] transition-all disabled:opacity-50"
      >
        {uploading ? <Loader2 size={13} className="animate-spin" /> : <UploadIcon size={13} />}
        {uploading ? "Lädt hoch…" : "Vertrag hochladen"}
      </button>
    </div>
  );
};
